// Tyre Size & Speedometer Error Calculator: field metadata, client-side validation, presets,
// the sidewall size-string parser/formatter, per-segment plain-language explanations, and
// the prefill contracts for the Gear Ratio / Vehicle Speed -> RPM tools. The physics and
// authoritative validation live server-side -- see src/lib/toolsApi.js (tyreSizeToolApi)
// and backend/tools/tyre_size_tool.py. min/max/step mirror
// backend/core/lookup/tyre_size_specs.py exactly.

export const KMPH_TO_MPH = 1 / 1.609344;

export const TYRES = [
  { prefix: 'orig', label: 'Original' },
  { prefix: 'new', label: 'New' },
];

// Per-tyre fields; the full key is `${prefix}${key}`, e.g. newWidthMm.
export const TYRE_FIELD_SPECS = [
  { key: 'WidthMm', part: 'width', label: 'Width', unit: 'mm', min: 105, max: 395, step: 5, strictlyPositive: true },
  { key: 'AspectPct', part: 'aspect', label: 'Aspect Ratio', unit: '%', min: 20, max: 90, step: 5, strictlyPositive: true },
  { key: 'RimIn', part: 'rim', label: 'Rim Diameter', unit: 'in', min: 10, max: 24, step: 0.5, strictlyPositive: true },
];

export const GLOBAL_FIELD_SPECS = [
  { key: 'speedoOverReadPct', label: 'Original Speedometer Over-read', unit: '%', min: 0, max: 10, strictlyPositive: false },
  { key: 'annualDistanceKm', label: 'Annual Distance', unit: 'km', min: 100, max: 200000, strictlyPositive: true },
  { key: 'cruiseSpeedKmph', label: 'Cruise Speed', unit: 'km/h', min: 10, max: 250, strictlyPositive: true },
];

export const DYNAMIC_FACTOR_SPEC = { key: 'dynamicFactor', label: 'Dynamic Radius Factor', unit: '', min: 0.93, max: 1.0, strictlyPositive: true };

export const DRIVELINE_FIELD_SPECS = [
  { key: 'gearRatio', label: 'Gear Ratio', unit: ':1', min: 0.3, max: 15.0, strictlyPositive: true },
  { key: 'finalDriveRatio', label: 'Final Drive Ratio', unit: ':1', min: 1.0, max: 12.0, strictlyPositive: true },
];

export const DEFAULT_INPUTS = {
  orig: { widthMm: 205, aspectPct: 55, rimIn: 16, zr: false, loadIndex: 91, speedSymbol: 'V' },
  new: { widthMm: 225, aspectPct: 45, rimIn: 17, zr: false, loadIndex: 94, speedSymbol: 'W' },
  radiusBasis: 'geometric',
  dynamicFactor: 0.97,
  speedoOverReadPct: 0,
  annualDistanceKm: 15000,
  cruiseSpeedKmph: 100,
  gearRatio: '',
  finalDriveRatio: '',
};

export const PRESETS = [
  {
    key: 'plus_one_hatch',
    label: 'Plus-one: 205/55R16 → 225/45R17',
    orig: { widthMm: 205, aspectPct: 55, rimIn: 16, zr: false, loadIndex: 91, speedSymbol: 'V' },
    new: { widthMm: 225, aspectPct: 45, rimIn: 17, zr: false, loadIndex: 94, speedSymbol: 'W' },
  },
  {
    key: 'plus_one_compact',
    label: 'Plus-one: 195/65R15 → 205/55R16',
    orig: { widthMm: 195, aspectPct: 65, rimIn: 15, zr: false, loadIndex: 91, speedSymbol: 'H' },
    new: { widthMm: 205, aspectPct: 55, rimIn: 16, zr: false, loadIndex: 91, speedSymbol: 'V' },
  },
  {
    key: 'suv_taller',
    label: 'SUV taller: 265/70R17 → 285/70R17',
    orig: { widthMm: 265, aspectPct: 70, rimIn: 17, zr: false, loadIndex: 115, speedSymbol: 'T' },
    new: { widthMm: 285, aspectPct: 70, rimIn: 17, zr: false, loadIndex: 116, speedSymbol: 'T' },
  },
  {
    key: 'winter_minus_one',
    label: 'Winter: 225/45R18 → 205/55R17',
    orig: { widthMm: 225, aspectPct: 45, rimIn: 18, zr: false, loadIndex: 95, speedSymbol: 'W' },
    new: { widthMm: 205, aspectPct: 55, rimIn: 17, zr: false, loadIndex: 91, speedSymbol: 'H' },
  },
];

// Load index -> kg (ISO 4000-1 / ETRTO; see backend/core/lookup/tyre_size_specs.py for
// the sources). Kept client-side only for the segment explanations; the backend decodes
// authoritatively.
export const LOAD_INDEX_KG = {
  50: 190, 51: 195, 52: 200, 53: 206, 54: 212, 55: 218, 56: 224, 57: 230, 58: 236, 59: 243,
  60: 250, 61: 257, 62: 265, 63: 272, 64: 280, 65: 290, 66: 300, 67: 307, 68: 315, 69: 325,
  70: 335, 71: 345, 72: 355, 73: 365, 74: 375, 75: 387, 76: 400, 77: 412, 78: 425, 79: 437,
  80: 450, 81: 462, 82: 475, 83: 487, 84: 500, 85: 515, 86: 530, 87: 545, 88: 560, 89: 580,
  90: 600, 91: 615, 92: 630, 93: 650, 94: 670, 95: 690, 96: 710, 97: 730, 98: 750, 99: 775,
  100: 800, 101: 825, 102: 850, 103: 875, 104: 900, 105: 925, 106: 950, 107: 975, 108: 1000, 109: 1030,
  110: 1060, 111: 1090, 112: 1120, 113: 1150, 114: 1180, 115: 1215, 116: 1250, 117: 1285, 118: 1320, 119: 1360,
  120: 1400, 121: 1450, 122: 1500, 123: 1550, 124: 1600, 125: 1650, 126: 1700, 127: 1750, 128: 1800, 129: 1850,
  130: 1900,
};

export const SPEED_SYMBOL_KMPH = {
  L: 120, M: 130, N: 140, P: 150, Q: 160, R: 170, S: 180, T: 190, U: 200, H: 210, V: 240, W: 270, Y: 300, '(Y)': 300,
};

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

export function validateField(spec, rawValue) {
  if (rawValue === '' || rawValue === null || rawValue === undefined) return 'Required.';
  const value = Number(rawValue);
  if (Number.isNaN(value)) return 'Must be a number.';
  if (spec.strictlyPositive && value <= 0) return 'Must be greater than 0.';
  if (spec.min !== null && value < spec.min) return `Must be ≥ ${spec.min}.`;
  if (spec.max !== null && value > spec.max) return `Must be ≤ ${spec.max}.`;
  if (spec.step) {
    const ratio = value / spec.step;
    if (Math.abs(ratio - Math.round(ratio)) > 1e-6) return `Must be in steps of ${spec.step} ${spec.unit}.`;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Size string parser / formatter
// ---------------------------------------------------------------------------

const FLOTATION_RE = /^\s*\d{2}(?:[.,]\d+)?\s*[xX×]\s*\d/;
const CORE_RE = /^(P|LT|T)?\s*(\d{2,3})\s*\/\s*(\d{2})\s*(ZR|R|D|B|-)\s*(\d{2}(?:[.,]\d)?)\s*(C)?(.*)$/;
const SERVICE_RE = /^(\()?\s*(\d{2,3})(?:\s*\/\s*\d{2,3})?\s*(\(\s*[A-Z]\s*\)|[A-Z])?\s*(\))?$/;
const SUFFIX_RE = /\s*\b(XL|RF|RFT|EXTRA\s+LOAD|REINFORCED|M\+S|M\/S)\s*$/;

export const PARSE_HELP = 'Use the sidewall format: width/aspect R rim, e.g. 225/45R17 or 225/45 R17 94V.';

// Parses a sidewall size string such as "225/45R17", "225/45 R17 94V", "P225/45ZR17",
// "225/45ZR17 (94Y)" or "LT265/70R17 121/118S". Returns
// { ok: true, widthMm, aspectPct, rimIn, zr, loadIndex, speedSymbol } or { ok: false, error }.
// An empty string returns { ok: false, empty: true } (no error shown).
export function parseTyreSize(input) {
  const text = String(input ?? '').trim().toUpperCase().replace(/\s+/g, ' ');
  if (!text) return { ok: false, empty: true };
  if (FLOTATION_RE.test(text)) {
    return { ok: false, error: 'Flotation sizes like 31x10.50R15 aren’t supported yet. Enter a metric size such as 265/70R17.' };
  }
  const core = CORE_RE.exec(text);
  if (!core) return { ok: false, error: `Couldn’t read “${String(input).trim()}”. ${PARSE_HELP}` };
  const [, , width, aspect, construction, rim, , restRaw] = core;
  if (construction !== 'R' && construction !== 'ZR') {
    return { ok: false, error: 'Only radial sizes (R or ZR) are supported, e.g. 225/45R17.' };
  }

  let rest = restRaw.trim();
  while (SUFFIX_RE.test(rest)) rest = rest.replace(SUFFIX_RE, '').trim();

  let loadIndex = null;
  let speedSymbol = null;
  if (rest) {
    const service = SERVICE_RE.exec(rest);
    if (!service) return { ok: false, error: `Couldn’t read the load index / speed symbol “${rest}”. Expected e.g. 94V or (94Y).` };
    const [, openParen, li, symbolRaw, closeParen] = service;
    if (Boolean(openParen) !== Boolean(closeParen)) {
      return { ok: false, error: `Unbalanced brackets in “${rest}”. Expected e.g. (94Y).` };
    }
    loadIndex = Number(li);
    if (!(loadIndex in LOAD_INDEX_KG)) return { ok: false, error: `Load index ${li} is outside the supported table (50–130).` };
    if (symbolRaw) {
      const letter = symbolRaw.replace(/[()\s]/g, '');
      const bracketed = symbolRaw.startsWith('(') || Boolean(openParen);
      speedSymbol = bracketed ? `(${letter})` : letter;
      if (!(speedSymbol in SPEED_SYMBOL_KMPH)) {
        return { ok: false, error: `Unknown speed symbol “${symbolRaw.replace(/\s/g, '')}”. Supported: L–Y and (Y).` };
      }
    }
  }

  return {
    ok: true,
    widthMm: Number(width),
    aspectPct: Number(aspect),
    rimIn: Number(rim.replace(',', '.')),
    zr: construction === 'ZR',
    loadIndex,
    speedSymbol,
  };
}

const fmtNum = (v) => {
  if (v === '' || v === null || v === undefined) return '';
  const n = Number(v);
  return Number.isFinite(n) ? String(n) : String(v);
};

// Size parts -> [{ text, part }] segments; `part` is null for separators.
export function sizeSegments({ widthMm, aspectPct, rimIn, zr, loadIndex, speedSymbol }) {
  const segs = [
    { text: fmtNum(widthMm), part: 'width' },
    { text: '/', part: null },
    { text: fmtNum(aspectPct), part: 'aspect' },
    { text: zr ? 'ZR' : 'R', part: 'construction' },
    { text: fmtNum(rimIn), part: 'rim' },
  ];
  if (loadIndex !== null && loadIndex !== undefined && loadIndex !== '') {
    segs.push({ text: ' ', part: null }, { text: String(loadIndex), part: 'load' });
    if (speedSymbol) segs.push({ text: speedSymbol, part: 'speed' });
  } else if (speedSymbol) {
    segs.push({ text: ' ', part: null }, { text: speedSymbol, part: 'speed' });
  }
  return segs;
}

export function formatTyreSize(parts) {
  return sizeSegments(parts).map((s) => s.text).join('');
}

// ---------------------------------------------------------------------------
// Plain-language explanations for each size segment
// ---------------------------------------------------------------------------

const f1 = (n) => (Math.round(n * 10) / 10).toLocaleString(undefined, { maximumFractionDigits: 1 });

export const PART_TITLES = {
  width: 'Section width',
  aspect: 'Aspect ratio → sidewall',
  construction: 'Construction',
  rim: 'Rim diameter',
  load: 'Load index',
  speed: 'Speed symbol',
};

export function explainPart(part, t) {
  const w = Number(t.widthMm);
  const a = Number(t.aspectPct);
  const r = Number(t.rimIn);
  switch (part) {
    case 'width':
      return `${fmtNum(t.widthMm)} = section width: ${fmtNum(t.widthMm)} mm across the widest point of the sidewalls, measured on the recommended rim.`;
    case 'aspect':
      return Number.isFinite(w * a)
        ? `${fmtNum(t.aspectPct)} = the sidewall height is ${fmtNum(t.aspectPct)} % of the width (${fmtNum(t.widthMm)} mm × ${fmtNum(t.aspectPct)} % = ${f1((w * a) / 100)} mm).`
        : `${fmtNum(t.aspectPct)} = the sidewall height as a % of the width.`;
    case 'construction':
      return t.zr
        ? 'ZR = radial construction, rated above 240 km/h (an older marking; modern ZR tyres also carry W, Y or (Y)).'
        : 'R = radial construction: the body plies run straight across from bead to bead, at right angles to the tread.';
    case 'rim':
      return Number.isFinite(r)
        ? `${fmtNum(t.rimIn)} = fits a ${fmtNum(t.rimIn)}-inch rim (${f1(r * 25.4)} mm diameter where the tyre bead sits).`
        : 'Rim diameter in inches.';
    case 'load': {
      const kg = LOAD_INDEX_KG[t.loadIndex];
      return kg ? `${t.loadIndex} = load index: up to ${kg} kg per tyre at the specified inflation pressure.` : 'Load index.';
    }
    case 'speed': {
      const kmph = SPEED_SYMBOL_KMPH[t.speedSymbol];
      if (!kmph) return 'Speed symbol.';
      const above = t.speedSymbol.startsWith('(');
      return `${t.speedSymbol} = speed symbol: rated for ${above ? 'more than ' : 'up to '}${kmph} km/h (${Math.round(kmph * KMPH_TO_MPH)} mph)${above ? ', with the exact limit set by the tyre maker' : ''}.`;
    }
    default:
      return '';
  }
}

// ---------------------------------------------------------------------------
// Cross-tool prefill contracts (approved 2026-09-30)
//   Vehicle Speed -> RPM:  /tools/vehicle-speed-rpm?wheelRadiusM=0.3172
//   Gear Ratio:            /tools/gear-ratio-calculator?tireWidthMm=225&tireAspectRatio=45&wheelDiameterIn=17
// Limits mirror each target tool's own field specs; a button is disabled when the new
// tyre falls outside them.
// ---------------------------------------------------------------------------

export const SPEED_RPM_RADIUS_LIMITS = { min: 0.2, max: 0.5 };
export const GEAR_RATIO_TYRE_LIMITS = {
  tireWidthMm: { min: 100, max: 355 },
  tireAspectRatio: { min: 25, max: 85 },
  wheelDiameterIn: { min: 10, max: 24 },
};

export function speedRpmPrefillUrl(rollingRadiusMm) {
  // Round half-up in 0.1 mm units (integers) so e.g. 317.15 mm -> 0.3172 m, matching the
  // displayed 317.2 mm; (0.31715).toFixed(4) would give 0.3171 through binary float error.
  const radiusM = Math.round(rollingRadiusMm * 10) / 10000;
  const within = radiusM >= SPEED_RPM_RADIUS_LIMITS.min && radiusM <= SPEED_RPM_RADIUS_LIMITS.max;
  return {
    url: `/tools/vehicle-speed-rpm?wheelRadiusM=${radiusM.toFixed(4)}`,
    disabledReason: within ? null : `Rolling radius ${radiusM.toFixed(3)} m is outside that tool's 0.20–0.50 m range.`,
  };
}

export function gearRatioPrefillUrl({ widthMm, aspectPct, rimIn }) {
  const params = { tireWidthMm: widthMm, tireAspectRatio: aspectPct, wheelDiameterIn: rimIn };
  const outside = Object.entries(params).filter(([k, v]) => v < GEAR_RATIO_TYRE_LIMITS[k].min || v > GEAR_RATIO_TYRE_LIMITS[k].max);
  const names = { tireWidthMm: 'width 100–355 mm', tireAspectRatio: 'aspect 25–85 %', wheelDiameterIn: 'rim 10–24 in' };
  return {
    url: `/tools/gear-ratio-calculator?${new URLSearchParams(Object.entries(params).map(([k, v]) => [k, String(v)])).toString()}`,
    disabledReason: outside.length ? `That tool accepts ${outside.map(([k]) => names[k]).join(', ')}.` : null,
  };
}
